export const POD_VALIDATION_STATUS = {
  SETUP_REQUIRED: "setup_required",
  INVALID: "invalid",
  READY: "ready",
} as const;

export type PodValidationStatus =
  (typeof POD_VALIDATION_STATUS)[keyof typeof POD_VALIDATION_STATUS];

export interface PodValidationError {
  error_code?: string;
  error_message?: string;
  // commonsku product_sku_ids this error applies to. Empty => product/decoration-level
  // (applies to the whole item). A subset => variant-specific.
  order_item_group_ids?: string[];
}

// Maps a commonsku product_sku_id to a human label (e.g. "Kelly Green / 4XL").
export type VariantLabelById = Record<string, string>;

export interface PodValidationResponse {
  is_valid: boolean;
  errors: PodValidationError[];
  pod_shop_item_validation?: PodShopItemValidation;
}

export interface PodShopItemValidation {
  status: PodValidationStatus;
  is_valid: boolean;
  errors: PodValidationError[];
  error_message: string;
}

const getPodValidationErrorMessage = (error: PodValidationError): string => {
  const message = (error.error_message ?? "").trim();
  if (message) {
    return message;
  }

  const code = (error.error_code ?? "").trim();
  return code || "Validation failed";
};

// Append the affected variant labels when an error is variant-specific. Attribution is a
// best-effort enhancement — whenever it can't be done reliably we fall back to showing the
// raw message as-is (rendered as one bullet in the modal's list), which is always correct.
// Bare message (no "affects") is shown when:
//   - the error has no group ids (FulfillEngine didn't append one, or it couldn't be parsed);
//   - the error applies to every selected variant (product/decoration-level);
//   - any affected id can't be resolved to a human label (missing/unknown variant), since a
//     partial "affects" list would misleadingly hide the variants we couldn't name.
const formatPodValidationError = (
  error: PodValidationError,
  variantLabelById?: VariantLabelById,
): string => {
  const base = getPodValidationErrorMessage(error);
  const ids = error.order_item_group_ids ?? [];
  if (!variantLabelById || ids.length === 0) {
    return base;
  }

  const totalVariants = Object.keys(variantLabelById).length;
  if (totalVariants > 0 && ids.length >= totalVariants) {
    return base;
  }

  const labels = ids.map((id) => variantLabelById[id]);
  if (labels.some((label) => !label)) {
    return base;
  }

  return `${base} (affects: ${labels.join(", ")})`;
};

export const getPodValidationFromResponse = (
  response: PodValidationResponse,
): PodShopItemValidation => {
  const errors = response.errors || [];

  return {
    status: response.is_valid
      ? POD_VALIDATION_STATUS.READY
      : POD_VALIDATION_STATUS.INVALID,
    is_valid: response.is_valid,
    errors,
    error_message: errors.map(getPodValidationErrorMessage).join("\n"),
  };
};

export const getPodValidationStatus = (item: {
  pod_shop_item_validation?: Partial<PodShopItemValidation> | null;
}): PodValidationStatus => {
  const validation = item.pod_shop_item_validation;

  if (!validation) {
    return POD_VALIDATION_STATUS.SETUP_REQUIRED;
  }

  if (validation.status === POD_VALIDATION_STATUS.READY || validation.is_valid) {
    return POD_VALIDATION_STATUS.READY;
  }

  if (validation.status === POD_VALIDATION_STATUS.INVALID || validation.is_valid === false) {
    return POD_VALIDATION_STATUS.INVALID;
  }

  return POD_VALIDATION_STATUS.SETUP_REQUIRED;
};

export const getPodValidationErrors = (
  validation?: Partial<PodShopItemValidation> | null,
  variantLabelById?: VariantLabelById,
): string[] => {
  if (!validation) {
    return [];
  }

  if (validation.errors?.length) {
    return validation.errors
      .map((error) => formatPodValidationError(error, variantLabelById))
      .filter(Boolean);
  }

  return validation.error_message ? validation.error_message.split("\n").filter(Boolean) : [];
};

export const getPodHiddenUpdateForValidation = (
  previousStatus: PodValidationStatus | null | undefined,
  nextStatus: PodValidationStatus,
): 0 | 1 | null => {
  if (nextStatus !== POD_VALIDATION_STATUS.READY) {
    return 1;
  }

  if (previousStatus === POD_VALIDATION_STATUS.READY) {
    return null;
  }

  return 0;
};

export const canTogglePodItemHidden = (
  isPodShop: boolean | undefined,
  item: { pod_shop_item_validation?: Partial<PodShopItemValidation> | null },
): boolean => {
  if (!isPodShop) {
    return true;
  }

  return getPodValidationStatus(item) === POD_VALIDATION_STATUS.READY;
};
